import { __rest, __assign } from 'tslib';
import React, { forwardRef, useRef, useState, useCallback, useEffect, Fragment } from 'react';
import mergeRefs from 'react-merge-refs';
import { useSwipeable } from 'react-swipeable';
import cn from 'classnames';
import elementClosest from 'element-closest';
import { Portal } from '@alfalab/core-components-portal/esm';
import { Stack } from '@alfalab/core-components-stack/esm';
import { ToastPlateDesktop } from '@alfalab/core-components-toast-plate/esm/desktop';
import { stackingOrder } from '@alfalab/stack-context';
import { useClickOutside } from './utils/index.js';

var styles = {"notificationComponent":"notification__notificationComponent_df83f","isVisible":"notification__isVisible_df83f","isClosing":"notification__isClosing_df83f","toastContent":"notification__toastContent_df83f","actionSection":"notification__actionSection_df83f"};
require('./index.css')

var notificationClassNameSelector = ".".concat(styles.notificationComponent);
var Notification = forwardRef(function (_a, ref) {
    var className = _a.className, actionSectionClassName = _a.actionSectionClassName, children = _a.children, visible = _a.visible, _b = _a.offset, offset = _b === void 0 ? 108 : _b, _c = _a.hasCloser, hasCloser = _c === void 0 ? true : _c, _d = _a.autoCloseDelay, autoCloseDelay = _d === void 0 ? 5000 : _d, _e = _a.usePortal, usePortal = _e === void 0 ? true : _e, _f = _a.zIndex, zIndex = _f === void 0 ? stackingOrder.TOAST : _f, style = _a.style, _g = _a.position, position = _g === void 0 ? 'top' : _g, onClose = _a.onClose, onCloseTimeout = _a.onCloseTimeout, onMouseEnter = _a.onMouseEnter, onMouseLeave = _a.onMouseLeave, onClickOutside = _a.onClickOutside, restProps = __rest(_a, ["className", "actionSectionClassName", "children", "visible", "offset", "hasCloser", "autoCloseDelay", "usePortal", "zIndex", "style", "position", "onClose", "onCloseTimeout", "onMouseEnter", "onMouseLeave", "onClickOutside"]);
    var notificationRef = useRef(null);
    var autoCloseTimeoutRef = useRef(0);
    var closeTimeoutRef = useRef(0);
    var _h = useState(false), isClosing = _h[0], setIsClosing = _h[1];
    var startAutoCloseTimer = useCallback(function () {
        if (autoCloseDelay !== null) {
            autoCloseTimeoutRef.current = window.setTimeout(function () {
                if (onCloseTimeout) {
                    onCloseTimeout();
                }
            }, autoCloseDelay);
        }
    }, [autoCloseDelay, onCloseTimeout]);
    var stopAutoCloseTimer = useCallback(function () {
        clearTimeout(autoCloseTimeoutRef.current);
    }, []);
    useEffect(function () { return function () {
        clearTimeout(closeTimeoutRef.current);
    }; }, []);
    useEffect(function () {
        elementClosest(window);
    }, []);
    useEffect(function () {
        if (visible) {
            startAutoCloseTimer();
        }
        return function () {
            stopAutoCloseTimer();
        };
    }, [startAutoCloseTimer, stopAutoCloseTimer, visible]);
    var handleMouseEnter = useCallback(function (event) {
        stopAutoCloseTimer();
        if (onMouseEnter) {
            onMouseEnter(event);
        }
    }, [onMouseEnter, stopAutoCloseTimer]);
    var handleMouseLeave = useCallback(function (event) {
        stopAutoCloseTimer();
        startAutoCloseTimer();
        if (onMouseLeave) {
            onMouseLeave(event);
        }
    }, [onMouseLeave, startAutoCloseTimer, stopAutoCloseTimer]);
    var handleOutsideClick = useCallback(function (event) {
        var isTargetNotification = !!event.target.closest(notificationClassNameSelector);
        /*
         * проверка isTargetNotification нужна для предотвращения срабатывания handleOutsideClick
         * при клике на другие нотификации, если их несколько на странице
         */
        if (onClickOutside && visible && !isTargetNotification) {
            onClickOutside(event);
        }
    }, [onClickOutside, visible]);
    useClickOutside(notificationRef, handleOutsideClick);
    var swipeableHandlers = useSwipeable({
        onSwiped: function (_a) {
            var dir = _a.dir;
            if (onClose && ['Left', 'Right', 'Up'].includes(dir)) {
                setIsClosing(true);
                closeTimeoutRef.current = window.setTimeout(function () {
                    setIsClosing(false);
                    onClose();
                }, 100);
            }
        },
        delta: 100,
    });
    var Wrapper = usePortal ? Portal : Fragment;
    return (React.createElement(Stack, { value: zIndex }, function (computedZIndex) {
        var _a, _b;
        return (React.createElement(Wrapper, null,
            React.createElement("div", __assign({}, swipeableHandlers),
                React.createElement(ToastPlateDesktop, __assign({ className: cn(styles.notificationComponent, (_a = {},
                        _a[styles.isVisible] = visible,
                        _a[styles.isClosing] = isClosing,
                        _a), className), contentClassName: styles.toastContent, actionSectionClassName: cn(actionSectionClassName, styles.actionSection), style: __assign((_b = {}, _b[position] = offset, _b.zIndex = computedZIndex, _b), style), onMouseEnter: handleMouseEnter, onMouseLeave: handleMouseLeave, ref: mergeRefs([ref, notificationRef]), role: visible ? 'alert' : undefined, hasCloser: hasCloser, onClose: onClose }, restProps), children))));
    }));
});
Notification.displayName = 'Notification';

export { Notification };
